import { Autocomplete, FormControl, InputLabel, TextField } from '@mui/material';
import { AutocompleteRenderInputParams } from '@mui/material/Autocomplete/Autocomplete';
import classNames from 'classnames';
import { FocusEvent, MouseEventHandler, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as IconChevron } from '../../../assets/icons/chevron.svg';
import { ReactComponent as IconClose } from '../../../assets/icons/icon-close.svg';
import styles from './combo-box.module.scss';

export interface ComboBoxProps {
  label: string;
  options: ComboBoxOptions[];
  showClearButton?: boolean;
  disabled?: boolean;
  onValueChange?: (option: ComboBoxOptions) => void;
}

export interface ComboBoxOptions {
  value: string;
  label: string;
}

export const ComboBoxComponent = ({
  label,
  options,
  showClearButton = false,
  disabled = false,
  onValueChange,
}: ComboBoxProps): JSX.Element => {
  const { t } = useTranslation();
  const generatedId = useId();
  const inputId = `combo-box-${generatedId}`;
  const labelId = `${inputId}-label`;

  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  // Must be null (not undefined) so MUI Autocomplete stays controlled and clears correctly
  const [selectedValue, setSelectedValue] = useState<ComboBoxOptions | null>(null);
  const [inputValue, setInputValue] = useState('');

  const hasSelection = selectedValue !== null;
  const labelShrink = open || hasSelection || focused || inputValue.length > 0;

  const emptyValue: ComboBoxOptions = { value: '', label: '' };

  const clearValue = (): void => {
    setSelectedValue(null);
    setInputValue('');
    onValueChange?.(emptyValue);
  };

  const onClickClearButton: MouseEventHandler = (event): void => {
    event.preventDefault();
    event.stopPropagation();
    clearValue();
  };

  const renderInput = (params: AutocompleteRenderInputParams): React.ReactNode => (
    <TextField
      {...params}
      id={inputId}
      className={classNames(styles.ComboBoxTextField, {
        [styles.HasLabel]: !!label,
        [styles.ItemSelected]: hasSelection || inputValue.length > 0,
        [styles.IsOpen]: open,
        [styles.HasClearButton]: showClearButton && hasSelection,
      })}
      inputProps={{
        ...params.inputProps,
        onFocus: (event): void => {
          setFocused(true);
          params.inputProps.onFocus?.(event as FocusEvent<HTMLInputElement>);
        },
        onBlur: (event): void => {
          setFocused(false);
          params.inputProps.onBlur?.(event as FocusEvent<HTMLInputElement>);
        },
      }}
    />
  );

  return (
    <div className={classNames(styles.ComboBoxContainer, { [styles.Disabled]: disabled })}>
      <FormControl fullWidth>
        {label && (
          <InputLabel id={labelId} htmlFor={inputId} shrink={labelShrink}>
            {label}
          </InputLabel>
        )}
        {showClearButton && hasSelection && (
          <button
            type="button"
            className={styles.ClearButton}
            onClick={onClickClearButton}
            disabled={disabled}
            tabIndex={-1}
            title={t('component.dropdown.clearValue')}
            aria-label={t('component.dropdown.clearValue')}
          >
            <IconClose aria-hidden="true" />
          </button>
        )}
        <Autocomplete
          id={inputId}
          className={classNames(styles.ComboBox, { [styles.Open]: open })}
          options={options}
          value={selectedValue}
          inputValue={inputValue}
          disabled={disabled}
          open={open}
          selectOnFocus={false}
          popupIcon={<IconChevron />}
          noOptionsText={t('component.comboBox.noOptions')}
          getOptionLabel={(option): string => option.label}
          isOptionEqualToValue={(option, value): boolean => option.value === value.value}
          onInputChange={(_, newInputValue): void => setInputValue(newInputValue)}
          onChange={(_, newValue): void => {
            setSelectedValue(newValue);
            if (newValue) {
              setInputValue(newValue.label);
              onValueChange?.(newValue);
            } else {
              setInputValue('');
              onValueChange?.(emptyValue);
            }
          }}
          onOpen={(): void => setOpen(true)}
          onClose={(): void => setOpen(false)}
          renderInput={renderInput}
          renderOption={(props, option): React.ReactNode => (
            <li {...props} className={classNames(props.className, styles.ComboBoxMenuItem)}>
              {option.label}
            </li>
          )}
          componentsProps={{
            paper: { className: styles.ComboBoxMenu },
          }}
        />
      </FormControl>
    </div>
  );
};
